"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

interface WhyPartnerCard {
	icon: string;
	titleKey: string;
	descKey: string;
}

interface WhyPartnerStat {
	valueKey: string;
	labelKey: string;
}

interface IndustryWhyPartnerProps {
	namespace: string;
	cards: readonly WhyPartnerCard[];
	stats: readonly WhyPartnerStat[];
	brandColor?: string;
	titleClassName?: string;
}

export default function IndustryWhyPartner({
	namespace,
	cards,
	stats,
	brandColor = "#64a525",
	titleClassName,
}: IndustryWhyPartnerProps) {
	const t = useTranslations(namespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 flex flex-col items-center justify-center gap-6 sm:mb-8 md:mb-10 lg:mb-12 lg:flex-row lg:items-center lg:gap-12">
					<div
						className="flex aspect-square shrink-0 flex-col items-center justify-center gap-1 rounded-lg px-6 py-5 text-white sm:px-8 sm:py-6"
						style={{ backgroundColor: brandColor }}
					>
						<svg className="mb-2 h-12 w-12" viewBox="0 0 100 100" fill="none" aria-hidden="true">
							<g transform="rotate(20, 50, 50)">
								<path
									d="M 40,29 L 46,11 Q 50,2 54,11 L 61,29 Q 62,33 66,33 L 87,33 Q 97,33 89,39 L 73,52 Q 70,54 71,58 L 78,80 Q 81,90 73,85 L 53,73 Q 50,71 47,73 L 27,85 Q 19,90 22,80 L 29,58 Q 30,54 27,52 L 11,39 Q 3,33 13,33 L 34,33 Q 38,33 40,29 Z"
									stroke="white"
									strokeWidth="3.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<circle cx="43" cy="46" r="3" stroke="white" strokeWidth="3" />
								<circle cx="57" cy="46" r="3" stroke="white" strokeWidth="3" />
								<path d="M41 58 Q50 67 59 58" stroke="white" strokeWidth="3" strokeLinecap="round" />
							</g>
						</svg>
						<p className="text-xs font-medium sm:text-sm">{t("whyPartnerBadgeLine1")}</p>
						<p className="mt-1 text-lg font-bold sm:text-xl">{t("whyPartnerBadgeLine2")}</p>
						<p className="text-xs sm:text-sm">{t("whyPartnerBadgeLine3")}</p>
					</div>

					<div>
						<h2
							className={`mb-3 text-2xl font-bold leading-normal text-black sm:mb-4 sm:text-[30px] sm:leading-snug md:text-4xl md:leading-snug ${
								titleClassName ?? ""
							}`}
						>
							{t("whyPartnerTitle")}
						</h2>
						<p className="text-sm leading-relaxed text-black md:text-base">{t("whyPartnerDesc")}</p>
					</div>
				</div>

				<div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
					{cards.map((card) => (
						<div
							key={card.titleKey}
							className="rounded-xl border border-[#c2c2c2] bg-[#f8fff7] p-6 transition-shadow duration-300 hover:shadow-md sm:p-7 md:px-6 md:py-10"
						>
							<div className="flex aspect-square h-20 w-20 overflow-hidden">
								<Image
									src={card.icon}
									alt="icon"
									className="scale-[2] object-cover"
									width={100}
									height={100}
								/>
							</div>
							<h3 className="mb-2 mt-4 text-base font-semibold text-black sm:mb-3 sm:text-lg">
								{t(card.titleKey)}
							</h3>
							<p className="text-sm leading-relaxed text-black md:text-base">{t(card.descKey)}</p>
						</div>
					))}
				</div>

				<div className="grid grid-cols-2 gap-6 pt-8 sm:grid-cols-4">
					{stats.map((stat) => (
						<div key={stat.valueKey} className="text-center">
							<p className="text-2xl font-bold sm:text-3xl md:text-4xl" style={{ color: brandColor }}>
								{t(stat.valueKey)}
							</p>
							<p className="mt-1 text-xs text-gray-600 sm:text-sm">{t(stat.labelKey)}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
